import { CollapseProps, Flex, message, notification, Select } from "antd";
import Search from "antd/es/input/Search";
import dayjs from "dayjs";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import {
    getCatalogFinGroupsDetailThunk,
    getCatalogFinGroupsThunk,
    getExportExportPromosThunk,
    getPromoActualByPoint,
} from "tm-shared/api";
import { getStorageInventoryGetThunk } from "tm-shared/api";
import { paymentMethodOptions } from "tm-shared/constants";
import {
    PaymentForm,
    Point,
    ProductOrderItem,
    PromoDetails,
    TaskRoute,
    TaskSaleAdditional,
} from "tm-shared/types";
import { countText, isPointDistribution } from "tm-shared/utils";

import { getPointStorageId } from "@/domains/point";
import { useCatalog } from "@/domains/product";
import {
    useAppDispatch,
    useAppSelector,
    useOfflineModeSelector,
    usePointPriceListSelector,
    usePointPromosSelector,
    useStorageInventorySelector,
    useTaskPayloadSelector,
} from "@/hooks/redux";
import { useQueueInfo } from "@/hooks/requestQueue/useQueueInfo";
import ExportDropdown from "@/pages/SV/RouteManagement/components/ExportDropdown";
import { useCreateCheckout } from "@/pages/WorkPlan/Tasks/hooks";
import { Loading } from "@/shared/feedback/Loading";
import { Header } from "@/shared/layout";
import { FixedCenteredLayout, MainContainer } from "@/shared/layout/Layout";
import { Meta } from "@/shared/seo";
import BaseButton from "@/shared/ui/BaseButton";
import { BlockLoader } from "@/shared/ui/BlockLoader";
import { PrimaryText, SecondaryText } from "@/shared/ui/Typography";
import { setSalePaymentForm, setSaleProduct } from "@/store/slices/progress";
import { setSaleQuery } from "@/store/slices/ui";

import { GeneralProductCard } from "../components/GeneralProductCard";
import {
    ButtonsWrapper,
    SearchWrapper,
    StyledFormWarning,
    StyledTypographyFormWarning,
} from "../shared";
import { StyledAccordionHeader, StyledPromoCollapse } from "./Styled";

// --- Constants ---
const promoType = {
    p: "Цінова акція",
    d: "Цінова акція",
    bl: "Кількісна пляшкова акція",
    bs: "Кількісна пляшкова акція",
    m: "Гривнева акція",
};

// --- Types ---
interface PromoCatalogPageProps {
    point: Point;
    task: TaskSaleAdditional;
}

// --- Component ---
export const PromoCatalogPage: FC<PromoCatalogPageProps> = ({
    point,
    task,
}) => {
    // --- App hooks ---
    const dispatch = useAppDispatch();
    const { products } = useCatalog();
    const { isRunning: isQueueRunning, requestQueue } = useQueueInfo();

    // --- Derived identifiers ---
    const taskId = task.id;
    const pointId = point.id;

    // --- Local state ---
    const [formTouched, setFormTouched] = useState(false);
    const [promoLoading, setPromoLoading] = useState(false);
    const [actualPromo, setActualPromo] = useState<PromoDetails[]>([]);
    const [messageApi, contextHolder] = message.useMessage();

    // --- Store selectors ---
    const searchQuery = useAppSelector(
        (state) => state.ui.saleAdditional.query,
    );
    const exportIndicator = useAppSelector(
        (state) => state.work.exportIndicator,
    );
    const currentPayload = useTaskPayloadSelector(
        TaskRoute.SaleAdditional,
        taskId,
    );
    const promos = usePointPromosSelector(pointId);
    const offlineMode = useOfflineModeSelector();

    // --- Derived identifiers ---
    const storageId = getPointStorageId(point) ?? "";

    // --- Store selectors ---
    const currentPriceList = usePointPriceListSelector(pointId) ?? {};
    const currentInventoryList = useStorageInventorySelector(storageId) ?? {};

    // --- Derived values ---
    const hasPendingTaskSync = requestQueue.some((q) => q.id === task.id);
    const currentProducts = useMemo(
        () => currentPayload?.products ?? {},
        [currentPayload?.products],
    );
    const currentPaymentForm = currentPayload?.paymentForm;
    const currentIgnoreRemainings = currentPayload?.ignoreRemainings ?? [];
    const promoCatalog = useMemo(() => {
        if (!offlineMode) {
            return actualPromo;
        }

        return promos;
    }, [offlineMode, actualPromo, promos]);

    // --- Task hooks ---
    const { handle: handleSendProductsToCheckout, isLoading } =
        useCreateCheckout({
            task,
            pointId,
            products: currentProducts,
            shouldIgnoreRemainings: currentIgnoreRemainings.length > 0,
        });

    // --- Handlers ---
    const handleProductCountChange = useCallback(
        (product: ProductOrderItem) => {
            dispatch(
                setSaleProduct({
                    taskId,
                    product,
                }),
            );
        },
        [dispatch, taskId],
    );

    // --- Render data ---
    const filteredPromoCatalog = useMemo(() => {
        if (!promoCatalog) return [];

        if (!searchQuery) return promoCatalog;

        const query = searchQuery.toLowerCase();

        return promoCatalog.filter((promo) => {
            const typeName = promoType?.[promo?.type]?.toLowerCase() || "";
            const description = promo?.description?.toLowerCase() || "";

            return typeName.includes(query) || description.includes(query);
        });
    }, [promoCatalog, searchQuery]);

    const items: CollapseProps["items"] = filteredPromoCatalog?.map((item) => {
        const totalStockBalance = Object.values(
            item?.context?.stockBalances,
        ).reduce((acc, el) => acc + el, 0);

        const promoProducts = Object.entries(item?.context?.stockBalances).map(
            ([uniqueCode, stockBalance]) => {
                const presentSkuBarcode =
                    item?.context?.presentSKU?.[uniqueCode] ?? "";

                return {
                    name:
                        products[presentSkuBarcode]?.name ?? presentSkuBarcode,
                    stockBalance,
                };
            },
        );

        const knownProducts = Object.values(item?.context?.SKU || []).reduce<
            string[]
        >((acc, sku) => {
            const product = products[sku];
            if (product?.name) acc.push(sku);
            return acc;
        }, []);

        return {
            key: item?.id,
            label: (
                <StyledAccordionHeader
                    $isZeroPresents={totalStockBalance === 0}
                    $isExternal={item?.isExternal}
                >
                    <Flex vertical justify="center" align="center">
                        <Flex
                            vertical
                            align="flex-start"
                            justify="flex-start"
                            style={{ width: "100%", marginBottom: "10px" }}
                        >
                            <PrimaryText>
                                {`${promoType?.[item?.type]} до ${dayjs(
                                    item?.endDate,
                                ).format("YYYY-MM-DD")}`}{" "}
                            </PrimaryText>
                            <PrimaryText style={{ textWrap: "nowrap" }}>
                                {item?.context?.SKU?.length === 0
                                    ? "В акції беруть участь всі товари"
                                    : `В акції беруть участь ${countText.good(
                                          knownProducts.length,
                                      )}`}
                            </PrimaryText>
                        </Flex>
                        <SecondaryText>{item?.description}</SecondaryText>
                    </Flex>

                    {totalStockBalance > 0 ? (
                        <Flex vertical gap={10}>
                            <PrimaryText fontWeight="600">
                                Залишок подарунків:
                            </PrimaryText>

                            <Flex vertical gap={4}>
                                {promoProducts.map((item, index) => (
                                    <SecondaryText key={index}>
                                        {item?.name}: {item?.stockBalance}
                                    </SecondaryText>
                                ))}
                            </Flex>
                        </Flex>
                    ) : (
                        <Flex justify="center" align="center">
                            <PrimaryText textColor="error" fontWeight="600">
                                Подарунки закінчилися. Акція не спрацює!
                            </PrimaryText>
                        </Flex>
                    )}
                </StyledAccordionHeader>
            ),
            children: (
                <Flex vertical>
                    {Object.values(item?.context?.SKU).length === 0 ? (
                        <PrimaryText textAlign="center">
                            В акції беруть участь всі продукти. Будь ласка,
                            поверніться в продаж та оберіть потрібні товари
                        </PrimaryText>
                    ) : (
                        knownProducts.map((barcode) => {
                            const currentCount =
                                currentProducts[barcode]?.count ?? 0;
                            const isProductRemainingIgnored =
                                currentIgnoreRemainings.includes(barcode);

                            const currentProduct = products?.[barcode];

                            if (!currentProduct) return null;

                            return (
                                <GeneralProductCard
                                    key={barcode}
                                    {...currentProduct}
                                    pointId={pointId}
                                    price={
                                        currentPriceList?.[barcode]?.price ?? -1
                                    }
                                    oldPrice={
                                        currentPriceList?.[barcode]?.oldPrice ??
                                        0
                                    }
                                    hasSelectedPaymentForm={
                                        !!currentPaymentForm
                                    }
                                    itemsLeft={currentInventoryList?.[barcode]}
                                    value={currentCount}
                                    taskId={taskId}
                                    onCountChange={handleProductCountChange}
                                    isProductRemainingIgnored={
                                        isProductRemainingIgnored
                                    }
                                    isRemainingIgnored={
                                        currentIgnoreRemainings.length > 0
                                    }
                                />
                            );
                        })
                    )}
                </Flex>
            ),
        };
    });

    const handleCheckoutClick = () => {
        if (!currentPaymentForm) return;

        handleSendProductsToCheckout(currentPaymentForm);
    };

    const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        dispatch(setSaleQuery(e.target.value));
    };

    const handleExportPromos = useCallback(
        async ({ key }: { key: string }) => {
            switch (key) {
                case "excel": {
                    const clientId =
                        point && isPointDistribution(point)
                            ? point.typeContext.client.id
                            : "";

                    if (!clientId) {
                        notification.error({
                            title: "Неможливо здійснити експорт",
                            description: "Відсутній partnerId",
                        });
                        break;
                    }

                    dispatch(
                        getExportExportPromosThunk({
                            partnerId: clientId,
                        }),
                    );
                    break;
                }
            }

            messageApi.info(`Експорт усіх акцій партнера розпочато`);
        },
        [dispatch, messageApi, point],
    );

    // --- Effects ---
    useEffect(() => {
        if (storageId && !offlineMode) {
            dispatch(getStorageInventoryGetThunk({ storageId }));
            dispatch(getCatalogFinGroupsThunk());
            dispatch(getCatalogFinGroupsDetailThunk());
        }
    }, [dispatch, offlineMode, storageId]);

    useEffect(() => {
        if (!offlineMode) {
            const fetchPromo = async () => {
                try {
                    setPromoLoading(true);

                    const { result } = await getPromoActualByPoint({ pointId });

                    setActualPromo(result);
                } catch (error) {
                    console.error(error);
                } finally {
                    setPromoLoading(false);
                }
            };

            fetchPromo();
        }
    }, [dispatch, pointId, offlineMode]);

    // --- Render ---
    return (
        <>
            <Meta title="Акційні пропозиції" />
            <Header
                title="Акційні пропозиції"
                back
                rightActions={
                    <ExportDropdown
                        isLoading={!!exportIndicator}
                        loadingLabel={exportIndicator.toString()}
                        menuItems={[{ key: "excel", label: "Експорт в Excel" }]}
                        onMenuClick={handleExportPromos}
                    />
                }
            />
            {contextHolder}
            {formTouched && (
                <StyledFormWarning>
                    <StyledTypographyFormWarning>
                        Увага! Ви змінили форму оплати, продаж буде тільки по
                        ній
                    </StyledTypographyFormWarning>
                </StyledFormWarning>
            )}
            <SearchWrapper>
                <div>
                    <Search
                        placeholder="Пошук"
                        defaultValue={searchQuery}
                        onChange={onSearchChange}
                        allowClear
                        size="large"
                        style={{
                            marginRight: "6px",
                        }}
                    />
                </div>
                <Select
                    options={paymentMethodOptions}
                    value={currentPaymentForm}
                    disabled={promoLoading}
                    onChange={(value: PaymentForm) => {
                        if (!formTouched && currentPaymentForm)
                            setFormTouched(true);
                        dispatch(
                            setSalePaymentForm({
                                taskId,
                                paymentForm: value,
                            }),
                        );
                    }}
                    placeholder="Форма"
                    size="large"
                />
            </SearchWrapper>
            <MainContainer>
                {promoLoading && <BlockLoader text={"Завантаження акцій..."} />}

                {!promoLoading &&
                    (items?.length === 0 ? (
                        <PrimaryText>Акцій не знайдено</PrimaryText>
                    ) : (
                        <>
                            <StyledPromoCollapse
                                expandIconPlacement="start"
                                items={items}
                            />

                            <ButtonsWrapper>
                                {Object.values(currentProducts)?.length > 0 && (
                                    <FixedCenteredLayout
                                        bottom="5px"
                                        padding="0 10px"
                                    >
                                        <BaseButton
                                            type="primary"
                                            loading={isLoading}
                                            disabled={
                                                hasPendingTaskSync &&
                                                isQueueRunning
                                            }
                                            onClick={handleCheckoutClick}
                                        >
                                            Кошик
                                        </BaseButton>
                                    </FixedCenteredLayout>
                                )}
                            </ButtonsWrapper>
                        </>
                    ))}
            </MainContainer>

            {isLoading && <Loading title="Завершення задачі" />}
        </>
    );
};
